import { action, runInAction, makeObservable } from "mobx";
import FiltrosRapidosApi from "../api/FiltrosRapidosApi";
import ModalConfirmService from "../assistente-minutas/services/ModalConfirmService";
import { Minuta, Processo } from "../modelo";
import { FiltroEscaninho, IFiltroRapido } from "../modelo/FiltroRapido";
import UsuarioStore from '../store/UsuarioStore';
import ConfirmStore from "../store/ConfirmStore";
import FiltroStore from "../store/FiltroStore";
import MensagemStore from "../store/MensagemStore";
import ParametroStore from "../store/ParametroStore";
import ParametroUsuarioStore from "../store/ParametroUsuarioStore";
import RouterStore from "../store/RouterStore";
import DeepDiffMapper from "../utils/DeepDiffMapper";
import FiltrosRapidosService from "./FiltrosRapidosService";


class FiltroService {
    private ultimoLocalDoFiltroCarregado = '';
    private filtrosPorContexto = new Map<string, any[]>();
    private localDoFiltroAtual = () => {
        if (RouterStore.isMinutasAssinadas) {
            return 'minutasAssinadas';
        }
        if (RouterStore.isAlgumEscaninho) {
            return 'escaninho';
        }
        return '';
    }

    private dadosFiltroMinutasAssinadas = (fonte: any, usarFallbackStore: boolean = true) => {
        const fallbackStore = (campo: string, padrao: any = '') => {
            if (!usarFallbackStore) {
                return padrao;
            }
            const valorStore = (FiltroStore as any)[campo];
            return valorStore ?? padrao;
        };

        return {
            codigo: fonte?.codigo ?? fallbackStore('codigo', undefined),
            nome: fonte?.nome ?? fallbackStore('nomeFiltroEscaninho', ''),
            numeroProcessoFilter: fonte?.numeroProcessoFilter ?? fallbackStore('numeroProcessoFilter', ''),
            digitoProcessoFilter: fonte?.digitoProcessoFilter ?? fallbackStore('digitoProcessoFilter', ''),
            anoProcessoFilter: fonte?.anoProcessoFilter ?? fallbackStore('anoProcessoFilter', ''),
            orgaoProcessoFilter: fonte?.orgaoProcessoFilter ?? fallbackStore('orgaoProcessoFilter', ''),
            regionalProcessoFilter: fonte?.regionalProcessoFilter ?? fallbackStore('regionalProcessoFilter', ''),
            varaProcessoFilter: fonte?.varaProcessoFilter ?? fallbackStore('varaProcessoFilter', ''),
            filtroTipoMinuta: fonte?.filtroTipoMinuta ?? fallbackStore('filtroTipoMinuta', []),
            dataInicioMinutaAssinadaFiltro: fonte?.dataInicioMinutaAssinadaFiltro ?? fallbackStore('dataInicioMinutaAssinadaFiltro', ''),
            dataFimMinutaAssinadaFiltro: fonte?.dataFimMinutaAssinadaFiltro ?? fallbackStore('dataFimMinutaAssinadaFiltro', ''),
        };
    }

    private atualizarSnapshotFiltroMinutasAssinadas = (filtrosUsados: any, codigo?: number, nome?: string) => {
        const dadosNormalizados = this.limparDadosFiltro(
            this.dadosFiltroMinutasAssinadas(
                {
                    ...filtrosUsados,
                    codigo: codigo ?? filtrosUsados?.codigo,
                    nome: nome ?? filtrosUsados?.nome
                },
                false
            )
        );

        runInAction(() => {
            FiltroStore.filtroAplicadoMinutasAssinadasDados = dadosNormalizados;
            if (codigo !== undefined && codigo !== null) {
                FiltroStore.idFiltroUsado = codigo;
            }
            if (nome) {
                FiltroStore.nomeFiltroEscaninho = nome;
            }
        });
    }

    constructor() {
        makeObservable(this, {
            usarFiltro: action,
            fazerBuscaPadrao: action,
            diferencasDosFiltros: action
        });
    }

    compararCampoDeObjeto(valoresFiltro: any[], objeto: Partial<Processo> | Minuta, nomeCampoArray: string, nomeCampo: string, idCampo?: string): boolean {
        if (valoresFiltro && valoresFiltro.length > 0) {
            return valoresFiltro.find((valorFiltro) => {
                if (nomeCampoArray && objeto[nomeCampoArray] && objeto[nomeCampoArray].length > 0) {
                    return this.compararCampoDeObjetoTipoArray(valorFiltro, objeto, nomeCampoArray, nomeCampo, idCampo);
                } else if (idCampo) {
                    return this.compararCampoDeObjetoPorIdCampo(objeto, nomeCampo, idCampo, valorFiltro);
                } else {
                    return this.compararCampoDeObjetoPorNomeCampo(objeto, nomeCampo, valorFiltro);
                }
            });
        }
        return true;
    }

    compararCampoDeObjetoPorNomeCampo(objeto: Partial<Processo> | Minuta, nomeCampo: string, valorFiltro) {
        return objeto && (objeto[nomeCampo] === valorFiltro);
    }

    compararCampoDeObjetoPorIdCampo(objeto: Partial<Processo> | Minuta, nomeCampo: string, idCampo: string, valorFiltro) {
        let valorCampo = objeto && objeto[nomeCampo] && objeto[nomeCampo][idCampo];
        if (valorCampo && typeof valorCampo === 'string') {
            valorCampo = valorCampo.trim();
        }
        return valorCampo && valorCampo.trim() === valorFiltro.trim();
    }

    compararCampoDeObjetoTipoArray(valorFiltro: any, objeto: Partial<Processo> | Minuta, nomeCampoProcesso: string, nomeCampo: string, idCampo?: string): boolean {
        if (idCampo) {
            return objeto[nomeCampoProcesso].find((campoProc) => {
                const campo2 = campoProc[nomeCampo];
                return campo2 && typeof valorFiltro === 'string' ?
                    campo2[idCampo]?.toLocaleLowerCase() === (valorFiltro.toLocaleLowerCase())
                    :
                    campo2 != undefined && campo2[idCampo] === valorFiltro;
            });
        } else {
            return objeto[nomeCampoProcesso].find((campoProc) => {
                if (!campoProc) {
                    return false;
                }
                return typeof valorFiltro === 'string' ?
                    campoProc[nomeCampo]?.toLocaleLowerCase() === (valorFiltro.toLocaleLowerCase())
                    :
                    campoProc[nomeCampo] === valorFiltro;
            });
        }
    }

    salvarFiltro = async (edicao?: boolean, filtroParaSalvar?: FiltroEscaninho | IFiltroRapido) => {
        const filtroBase = (filtroParaSalvar || FiltroStore) as Partial<FiltroEscaninho>;
        const salvandoMinutasAssinadas = RouterStore.isMinutasAssinadas;
        const filtrosUsados: any = salvandoMinutasAssinadas
            ? this.dadosFiltroMinutasAssinadas(filtroBase)
            : FiltroEscaninho.inicializado(filtroBase as any);

        if (!salvandoMinutasAssinadas) {
            filtrosUsados.movimentosFilter = (filtrosUsados.movimentosFilter || [])
                .filter((movimento) => !!movimento && typeof movimento === 'string');
        }

        const filtroRapido = new IFiltroRapido();
        const codigoEdicao =
            filtrosUsados.codigo ||
            (filtroParaSalvar && (filtroParaSalvar as any).codigo) ||
            (FiltroStore as any).idFiltroUsado;
        filtroRapido.codigo = edicao ? codigoEdicao : undefined;
        const dadosLimpos = this.limparDadosFiltro(filtrosUsados);
        filtroRapido.dados = JSON.stringify(dadosLimpos);
        filtroRapido.localDoFiltro = this.localDoFiltroAtual();
        filtroRapido.cpf = UsuarioStore.login;
        filtroRapido.gabinete = UsuarioStore.gabinete;
        filtroRapido.nome = edicao ? (filtrosUsados.nome || FiltroStore.nomeFiltroEscaninho) : FiltroStore.nomeFiltroEscaninho;

        if (edicao && filtroParaSalvar && filtroParaSalvar.dados) {
            filtroRapido.dados = typeof filtroParaSalvar.dados === 'string'
                ? filtroParaSalvar.dados
                : JSON.stringify(filtroParaSalvar.dados);
        }

        if (filtroRapido.codigo) {
            await FiltrosRapidosService.alterarFiltro(filtroRapido);
            MensagemStore.apresentarMensagem('Filtro alterado com sucesso');
            await this.carregarFiltrosSalvos(true);
            if (salvandoMinutasAssinadas) {
                this.atualizarSnapshotFiltroMinutasAssinadas(dadosLimpos, filtroRapido.codigo, filtroRapido.nome);
            }
            return true;
        } else {
            const filtrosExistentes = await this.carregarFiltrosSalvos(true);
            if (!filtrosExistentes) {
                return false;
            }
            const mesmoNome = filtrosExistentes.find(filtro => filtro.nome === filtroRapido.nome);
            if (mesmoNome) {

                runInAction(() => {
                    ConfirmStore.titulo = 'Nome de filtro já existe';
                    ConfirmStore.corpo = `<div>Este nome de filtro já existe.</div>
                    <div> Se deja alterar o filtro existente, basta usá-lo, alterar e salvar novamente. Para mudar apenas o nome basta editar o nome na lista de filtros já salvos. </div>
                    <div>Deseja criar um novo filtro com o mesmo nome?</div>`;
                });
                ModalConfirmService.abrirModal();
                if (await ModalConfirmService.aguardarEscolhaDoUsuario()) {
                    await FiltrosRapidosService.salvarNovoFiltro(filtroRapido);
                    MensagemStore.apresentarMensagem('Filtro novo salvo');
                    await this.carregarFiltrosSalvos(true);
                    if (salvandoMinutasAssinadas) {
                        this.atualizarSnapshotFiltroMinutasAssinadas(dadosLimpos, undefined, filtroRapido.nome);
                    }
                    return true;
                }
                return false;
            } else {
                await FiltrosRapidosService.salvarNovoFiltro(filtroRapido);
                MensagemStore.apresentarMensagem('Filtro novo salvo');
                await this.carregarFiltrosSalvos(true);
                if (salvandoMinutasAssinadas) {
                    this.atualizarSnapshotFiltroMinutasAssinadas(dadosLimpos, undefined, filtroRapido.nome);
                }
                return true;
            }
        }
    }

    limparDadosFiltro = (filtro: Partial<FiltroEscaninho> | Record<string, any>) => {
        const dados: any = { ...filtro };
        const chavesRemover = [
            'codigo',
            'dados',
            'localDoFiltro',
            'cpf',
            'gabinete',
            'nome',
            'idFiltroUsado',
            'nomeFiltroEscaninho'
        ];
        chavesRemover.forEach((chave) => {
            delete dados[chave];
        });

        Object.keys(dados).forEach((chave) => {
            const valor = dados[chave];
            if (chave === 'camposDeTriagemBooleanFilter' && Array.isArray(valor)) {
                dados[chave] = valor
                    .filter((campo) => campo !== undefined && campo !== null)
                    .map((campo) => {
                        if (typeof campo === 'object' && campo !== null && 'valor' in campo) {
                            return campo.valor;
                        }
                        return campo;
                    });
            }
            if (chave === 'motivoImpedimentoSelecionadoFilter' && Array.isArray(valor)) {
                dados[chave] = valor
                    .filter((motivo) => !!motivo && motivo !== 'Qualquer tipo')
                    .map((motivo) => {
                        if (typeof motivo === 'number') {
                            return motivo;
                        }
                        const motivoEncontrado = ParametroStore.motivosImpedimentos
                            ?.find((item) => item?.texto === motivo);
                        return motivoEncontrado?.id ?? motivo;
                    });
            }
            if (chave === 'usuariosUltimaAlteracaoSituacaoMinutaFilter' && Array.isArray(valor)) {
                dados[chave] = valor
                    .filter((usuario) => !!usuario)
                    .map((usuario) => ({
                        login: usuario.login,
                        nome: usuario.nome
                    }));
            }
            if (chave === 'categoriaProcessoFilter' && Array.isArray(valor)) {
                dados[chave] = valor
                    .filter((categoria) => !!categoria)
                    .map((categoria) => ({
                        numero: categoria.numero,
                        descricao: categoria.descricao
                    }));
            }
            if (chave === 'etiquetaSelecionadaFilter' && Array.isArray(valor)) {
                dados[chave] = valor
                    .filter((etiqueta) => !!etiqueta)
                    .map((etiqueta) => ({
                        numero: etiqueta.numero,
                        texto: etiqueta.texto
                    }));
            }
            if (chave === 'tipoPeticaoSelecionadaFilter'
                && Array.isArray(valor)
                && valor.length === 1
                && valor[0] === 'Qualquer tipo'
                && !dados.possuiPeticaoPendente) {
                delete dados[chave];
                return;
            }
            if (valor === undefined || valor === null) {
                delete dados[chave];
                return;
            }
            if (typeof valor === 'string' && valor.trim() === '') {
                delete dados[chave];
                return;
            }
            if (typeof valor === 'boolean' && valor === false) {
                delete dados[chave];
                return;
            }
            if (Array.isArray(valor) && valor.length === 0) {
                delete dados[chave];
            }
        });

        return dados;
    }

    deletarFiltro = (filtro: IFiltroRapido) => {
        return FiltrosRapidosApi.excluirFiltro(filtro.codigo).then(() => {
            this.filtrosPorContexto.forEach((lista, contexto) => {
                const listaAtualizada = (lista || []).filter((item: any) => item.codigo !== filtro.codigo);
                this.filtrosPorContexto.set(contexto, listaAtualizada);
            });
            runInAction(() => {
                FiltroStore.filtrosEscaninho = FiltroStore.filtrosEscaninho
                    .filter((item) => item.codigo !== filtro.codigo);
            });
            MensagemStore.apresentarMensagem('Filtro deletado');
        });
    }

    async carregarFiltrosSalvos(force: boolean = false) {
        const localDoFiltro = this.localDoFiltroAtual();
        if (!localDoFiltro) {
            runInAction(() => {
                FiltroStore.filtrosEscaninho = [];
            });
            this.ultimoLocalDoFiltroCarregado = '';
            return [];
        }

        if (!force && this.filtrosPorContexto.has(localDoFiltro)) {
            const filtrosCache = this.filtrosPorContexto.get(localDoFiltro) || [];
            runInAction(() => {
                FiltroStore.filtrosEscaninho = filtrosCache as any;
            });
            this.ultimoLocalDoFiltroCarregado = localDoFiltro;
            return filtrosCache;
        }

        if (!force
            && this.ultimoLocalDoFiltroCarregado === localDoFiltro
            && FiltroStore.filtrosEscaninho?.length > 0) {
            return FiltroStore.filtrosEscaninho;
        }
        let filtrosEscaninho = await FiltrosRapidosApi.recuperarFiltrosDoUsuario(UsuarioStore.login, UsuarioStore.gabinete, localDoFiltro);
        filtrosEscaninho = (filtrosEscaninho || []).filter((filtro: any) => filtro?.localDoFiltro === localDoFiltro);
        this.filtrosPorContexto.set(localDoFiltro, filtrosEscaninho);
        runInAction(() => {
            FiltroStore.filtrosEscaninho = filtrosEscaninho as any;
        });
        this.ultimoLocalDoFiltroCarregado = localDoFiltro;
        return filtrosEscaninho;
    }

    usarFiltro(filtro: any, onAplicarFiltroMinutas?: () => void) {
        const cloneArray = (arr: any[]) => {
            if (!Array.isArray(arr)) {
                return [];
            }
            return arr.map((item) => {
                if (item && typeof item === 'object') {
                    return { ...item };
                }
                return item;
            });
        };
        const normalizarCamposDeTriagemBooleanFilter = (campos: any[]) => {
            const opcoes = FiltroStore.camposDeTriagemBooleanFormatoAutoComplete || [];
            return cloneArray(campos).map((campo) => {
                if (campo && typeof campo === 'object' && 'valor' in campo) {
                    return campo;
                }
                const opcao = opcoes.find((item) => item?.valor?.valor === campo);
                if (opcao?.valor) {
                    return { ...opcao.valor };
                }
                return {
                    descricao: String(campo),
                    valor: campo
                };
            });
        };
        const normalizarMotivoImpedimentoSelecionadoFilter = (motivos: any[]) => {
            return cloneArray(motivos).map((motivo) => {
                if (typeof motivo === 'number') {
                    const motivoEncontrado = ParametroStore.motivosImpedimentos
                        ?.find((item) => item?.id === motivo);
                    return motivoEncontrado?.texto ?? String(motivo);
                }
                if (typeof motivo === 'string') {
                    return motivo;
                }
                return String(motivo);
            });
        };

        if (RouterStore.isMinutasAssinadas) {
            this.usarFiltroMinutasAssinadas(filtro, cloneArray, onAplicarFiltroMinutas);
            return;
        }

        this.usarFiltroEscaninho(filtro, cloneArray, normalizarCamposDeTriagemBooleanFilter, normalizarMotivoImpedimentoSelecionadoFilter);
    }

    private usarFiltroMinutasAssinadas(filtro: any, cloneArray: (arr: any[]) => any[], onAplicarFiltroMinutas?: () => void) {
        FiltroStore.limparFiltrosMinutasAssinadas();

        FiltroStore.idFiltroUsado = filtro.codigo;
        FiltroStore.nomeFiltroEscaninho = filtro.nome;

        if (typeof filtro.dados === 'string') {
            filtro.dados = JSON.parse(String(filtro.dados));
        }

        FiltroStore.numeroProcessoFilter = filtro.dados.numeroProcessoFilter || '';
        FiltroStore.digitoProcessoFilter = filtro.dados.digitoProcessoFilter || '';
        FiltroStore.anoProcessoFilter = filtro.dados.anoProcessoFilter || '';
        FiltroStore.orgaoProcessoFilter = filtro.dados.orgaoProcessoFilter || '';
        FiltroStore.regionalProcessoFilter = filtro.dados.regionalProcessoFilter || '';
        FiltroStore.varaProcessoFilter = filtro.dados.varaProcessoFilter || '';
        FiltroStore.filtroTipoMinuta = cloneArray(filtro.dados.filtroTipoMinuta);
        FiltroStore.dataInicioMinutaAssinadaFiltro = filtro.dados.dataInicioMinutaAssinadaFiltro || '';
        FiltroStore.dataFimMinutaAssinadaFiltro = filtro.dados.dataFimMinutaAssinadaFiltro || '';
        FiltroStore.filtroAplicadoMinutasAssinadasDados = this.dadosFiltroMinutasAssinadas({
            ...filtro.dados,
            codigo: filtro.codigo,
            nome: filtro.nome
        }, false);

        FiltroStore.modalUsarFiltroAberto = false;
        FiltroStore.drawerIsOpen = false;

        if (onAplicarFiltroMinutas) {
            onAplicarFiltroMinutas();
        }
    }

    private usarFiltroEscaninho(
        filtro: any,
        cloneArray: (arr: any[]) => any[],
        normalizarCamposDeTriagemBooleanFilter: (campos: any[]) => any[],
        normalizarMotivoImpedimentoSelecionadoFilter: (motivos: any[]) => any[]
    ) {
        FiltroStore.limparFiltros();

        FiltroStore.idFiltroUsado = filtro.codigo;
        FiltroStore.nomeFiltroEscaninho = filtro.nome;

        if (typeof filtro.dados === 'string') {
            filtro.dados = JSON.parse(String(filtro.dados));
        }

        FiltroStore.origem = filtro.dados.origem || '';
        FiltroStore.possuiResponsavel = filtro.dados.possuiResponsavel || false;
        FiltroStore.naoPossuiResponsavel = filtro.dados.naoPossuiResponsavel || false;
        FiltroStore.possuiRedatorDesignado = filtro.dados.possuiRedatorDesignado || false;
        FiltroStore.processoRetornandoAoGabinete = filtro.dados.processoRetornandoAoGabinete || false;
        FiltroStore.possuiMinutaDespacho = filtro.dados.possuiMinutaDespacho || false;
        FiltroStore.possuiMinutaDecisao = filtro.dados.possuiMinutaDecisao || false;
        FiltroStore.possuiMinutaVoto = filtro.dados.possuiMinutaVoto || false;
        FiltroStore.possuiMinutaVotoAgravoProvido = filtro.dados.possuiMinutaVotoAgravoProvido || false;
        FiltroStore.naoPossuiMinuta = filtro.dados.naoPossuiMinuta || false;
        FiltroStore.foraDoGabineteFilter = filtro.dados.foraDoGabineteFilter || false;
        FiltroStore.incluirForaDoGabineteFilter = filtro.dados.incluirForaDoGabineteFilter || false;
        FiltroStore.julgadoFilter = filtro.dados.julgadoFilter || false;
        FiltroStore.naoJulgadoFilter = filtro.dados.naoJulgadoFilter || false;
        FiltroStore.tipoJulgamentoPresencialFilter = filtro.dados.tipoJulgamentoPresencialFilter || false;
        FiltroStore.tipoJulgamentoVirtualFilter = filtro.dados.tipoJulgamentoVirtualFilter || false;
        FiltroStore.publicavelFilter = filtro.dados.publicavelFilter || false;
        FiltroStore.naoPublicavelFilter = filtro.dados.naoPublicavelFilter || false;
        FiltroStore.favoritoFilter = filtro.dados.favoritoFilter || false;
        FiltroStore.naoFavoritoFilter = filtro.dados.naoFavoritoFilter || false;
        FiltroStore.votoLiberadoFilter = filtro.dados.votoLiberadoFilter || false;
        FiltroStore.votoNaoLiberadoFilter = filtro.dados.votoNaoLiberadoFilter || false;
        FiltroStore.possuiPeticaoPendente = filtro.dados.possuiPeticaoPendente || false;
        FiltroStore.naoPossuiPeticaoPendente = filtro.dados.naoPossuiPeticaoPendente || false;
        FiltroStore.possuiImpedimento = filtro.dados.possuiImpedimento || false;
        FiltroStore.assinavel = filtro.dados.assinavel || false;
        FiltroStore.orgaoJudicanteSelecionadoFilter = cloneArray(filtro.dados.orgaoJudicanteSelecionadoFilter);
        FiltroStore.andTramTarefasSelecionadoFilter = cloneArray(filtro.dados.andTramTarefasSelecionadoFilter);
        FiltroStore.tipoPeticaoSelecionadaFilter = cloneArray(filtro.dados.tipoPeticaoSelecionadaFilter);
        FiltroStore.motivoImpedimentoSelecionadoFilter = normalizarMotivoImpedimentoSelecionadoFilter(filtro.dados.motivoImpedimentoSelecionadoFilter);
        FiltroStore.tarefaSelecionadaFilter = cloneArray(filtro.dados.tarefaSelecionadaFilter);
        FiltroStore.usuarioSelecionadoFilter = cloneArray(filtro.dados.usuarioSelecionadoFilter);
        FiltroStore.etiquetaSelecionadaFilter = cloneArray(filtro.dados.etiquetaSelecionadaFilter);
        FiltroStore.indicadorSelecionadoFilter = cloneArray(filtro.dados.indicadorSelecionadoFilter);
        FiltroStore.categoriaProcessoFilter = cloneArray(filtro.dados.categoriaProcessoFilter);
        FiltroStore.situacaoMinutaFilter = cloneArray(filtro.dados.situacaoMinutaFilter);
        FiltroStore.unidadePublicadoraFilter = cloneArray(filtro.dados.unidadePublicadoraFilter);
        FiltroStore.resultadoJulgamentoFilter = cloneArray(filtro.dados.resultadoJulgamentoFilter);
        FiltroStore.listaDeNumeroProcessoFilter = filtro.dados.listaDeNumeroProcessoFilter || '';
        FiltroStore.textoTriagemFilter = filtro.dados.textoTriagemFilter || '';
        FiltroStore.tipoMinutaFilter = cloneArray(filtro.dados.tipoMinutaFilter);
        FiltroStore.classeProcessoFilter = cloneArray(filtro.dados.classeProcessoFilter);
        FiltroStore.temaProcessoFilter = cloneArray(filtro.dados.temaProcessoFilter);
        FiltroStore.classeInteiraFilter = cloneArray(filtro.dados.classeInteiraFilter);
        FiltroStore.classeInicialProcessoFilter = cloneArray(filtro.dados.classeInicialProcessoFilter);
        FiltroStore.numeroProcessoFilter = filtro.dados.numeroProcessoFilter || '';
        FiltroStore.digitoProcessoFilter = filtro.dados.digitoProcessoFilter || '';
        FiltroStore.anoProcessoFilter = filtro.dados.anoProcessoFilter || '';
        FiltroStore.orgaoProcessoFilter = filtro.dados.orgaoProcessoFilter || '';
        FiltroStore.regionalProcessoFilter = filtro.dados.regionalProcessoFilter || '';
        FiltroStore.varaProcessoFilter = filtro.dados.varaProcessoFilter || '';
        FiltroStore.assuntoFilter = cloneArray(filtro.dados.assuntoFilter);
        FiltroStore.nomeParteFilter = filtro.dados.nomeParteFilter || '';
        FiltroStore.parteFilter = filtro.dados.parteFilter || '';
        FiltroStore.pautaFilter = cloneArray(filtro.dados.pautaFilter);
        FiltroStore.movimentosFilter = cloneArray(filtro.dados.movimentosFilter)
            .filter((movimento) => !!movimento && typeof movimento === 'string');
        FiltroStore.anoAutuacaoFilter = cloneArray(filtro.dados.anoAutuacaoFilter);
        FiltroStore.situacaoPeticaoFilter = filtro.dados.situacaoPeticaoFilter || '';
        FiltroStore.camposDeTriagemBooleanFilter = normalizarCamposDeTriagemBooleanFilter(filtro.dados.camposDeTriagemBooleanFilter);
        FiltroStore.camposDeTriagemValorCondenacaoFilter = filtro.dados.camposDeTriagemValorCondenacaoFilter || '';
        FiltroStore.camposDeResponsavelPelaTriagemFilter = cloneArray(filtro.dados.camposDeResponsavelPelaTriagemFilter);
        FiltroStore.usuariosUltimaAlteracaoSituacaoMinutaFilter = cloneArray(filtro.dados.usuariosUltimaAlteracaoSituacaoMinutaFilter);
        FiltroStore.filtrosFilter = cloneArray(filtro.dados.filtrosFilter);
        FiltroStore.relatorProcessoFilter = cloneArray(filtro.dados.relatorProcessoFilter);
        FiltroStore.correJuntoFilter = filtro.dados.correJuntoFilter || false;
        FiltroStore.associadoFilter = filtro.dados.associadoFilter || false;

        const mostra = (filtroId: string) => ParametroUsuarioStore.mostraFiltro(filtroId);
        const podeRelator = (UsuarioStore.ehSecretario || RouterStore.isEscaninhoDeRedatorDesignado || RouterStore.isEscaninhoDeVotoVencido)
            && mostra('relator');
        const podeVotoLiberado = RouterStore.isEscaninhoJulgamento && mostra('votoLiberado');
        const podeAssinatura = RouterStore.isEscaninhoAssinatura && mostra('assinatura');

        if (!mostra('origem')) FiltroStore.origem = '';
        if (!mostra('numeroProcesso')) {
            FiltroStore.numeroProcessoFilter = '';
            FiltroStore.digitoProcessoFilter = '';
            FiltroStore.anoProcessoFilter = '';
            FiltroStore.orgaoProcessoFilter = '';
            FiltroStore.regionalProcessoFilter = '';
            FiltroStore.varaProcessoFilter = '';
        }
        if (!mostra('listaProcesso')) FiltroStore.listaDeNumeroProcessoFilter = '';
        if (!mostra('ojc')) FiltroStore.orgaoJudicanteSelecionadoFilter = [];
        if (!mostra('andamento')) FiltroStore.andTramTarefasSelecionadoFilter = [];
        if (!mostra('movimentacao')) FiltroStore.tarefaSelecionadaFilter = [];
        if (!mostra('anoAutuacao')) FiltroStore.anoAutuacaoFilter = [];
        if (!mostra('etiqueta')) FiltroStore.etiquetaSelecionadaFilter = [];
        if (!mostra('responsavel')) FiltroStore.usuarioSelecionadoFilter = [];
        if (!podeRelator) FiltroStore.relatorProcessoFilter = [];
        if (!mostra('possuiResponsavel')) {
            FiltroStore.possuiResponsavel = false;
            FiltroStore.naoPossuiResponsavel = false;
        }
        if (!mostra('indicador')) FiltroStore.indicadorSelecionadoFilter = [];
        if (!mostra('classe')) {
            FiltroStore.classeProcessoFilter = [];
            FiltroStore.classeInteiraFilter = [];
            FiltroStore.classeInicialProcessoFilter = [];
        }
        if (!mostra('assunto')) FiltroStore.assuntoFilter = [];
        if (!mostra('categoria')) FiltroStore.categoriaProcessoFilter = [];
        if (!mostra('inclusaoPauta')) FiltroStore.pautaFilter = [];
        if (!mostra('movimento')) FiltroStore.movimentosFilter = [];
        if (!mostra('partes')) {
            FiltroStore.nomeParteFilter = '';
            FiltroStore.parteFilter = '';
        }
        if (!mostra('resultadoJulgamento')) FiltroStore.resultadoJulgamentoFilter = [];
        if (!mostra('situacaoMinuta')) FiltroStore.situacaoMinutaFilter = [];
        if (!mostra('peticao')) {
            FiltroStore.possuiPeticaoPendente = false;
            FiltroStore.naoPossuiPeticaoPendente = false;
            FiltroStore.tipoPeticaoSelecionadaFilter = [];
        }
        if (!mostra('impedimento')) {
            FiltroStore.possuiImpedimento = false;
            FiltroStore.motivoImpedimentoSelecionadoFilter = [];
        }
        if (!mostra('tipoJulgamento')) {
            FiltroStore.tipoJulgamentoPresencialFilter = false;
            FiltroStore.tipoJulgamentoVirtualFilter = false;
        }
        if (!podeVotoLiberado) {
            FiltroStore.votoLiberadoFilter = false;
            FiltroStore.votoNaoLiberadoFilter = false;
        }
        if (!mostra('possuiMinuta')) {
            FiltroStore.possuiMinutaDespacho = false;
            FiltroStore.possuiMinutaDecisao = false;
            FiltroStore.possuiMinutaVoto = false;
            FiltroStore.possuiMinutaVotoAgravoProvido = false;
            FiltroStore.naoPossuiMinuta = false;
        }
        if (!mostra('unidadePublicadora')) FiltroStore.unidadePublicadoraFilter = [];
        if (!mostra('publicavel')) {
            FiltroStore.publicavelFilter = false;
            FiltroStore.naoPublicavelFilter = false;
        }
        if (!mostra('correJuntoAssociado')) {
            FiltroStore.correJuntoFilter = false;
            FiltroStore.associadoFilter = false;
        }
        if (!mostra('estrela')) {
            FiltroStore.favoritoFilter = false;
            FiltroStore.naoFavoritoFilter = false;
        }
        if (!mostra('redator')) FiltroStore.possuiRedatorDesignado = false;
        if (!mostra('retornoGabinete')) FiltroStore.processoRetornandoAoGabinete = false;
        if (!mostra('triagemLivre')) FiltroStore.textoTriagemFilter = '';
        if (!mostra('temas')) FiltroStore.temaProcessoFilter = [];
        if (!mostra('camposTriagem')) {
            FiltroStore.camposDeTriagemBooleanFilter = [];
            FiltroStore.camposDeTriagemValorCondenacaoFilter = '';
            FiltroStore.camposDeResponsavelPelaTriagemFilter = [];
        }
        if (!mostra('foraDoFluxo')) {
            FiltroStore.foraDoGabineteFilter = false;
            FiltroStore.incluirForaDoGabineteFilter = false;
        }
        if (!podeAssinatura) {
            FiltroStore.assinavel = false;
            FiltroStore.tipoMinutaFilter = [];
        }

        FiltroStore.modalUsarFiltroAberto = false;
        FiltroStore.drawerIsOpen = false;
        FiltroStore.procurar(true);
    }


    fazerBuscaPadrao = (limparFiltros: boolean = true) => {
        if (limparFiltros) {
            FiltroStore.limparFiltros();
        }
        FiltroStore.origem = 'PL';
        FiltroStore.procurar(true);
    };


    diferencasDosFiltros(filtroAtual) {
        const idFiltroAtual = filtroAtual?.idFiltroUsado;
        let filtro: any = FiltroStore.filtrosEscaninho.find(f => String((f as any).codigo) === String(idFiltroAtual));

        if (!filtro && RouterStore.isMinutasAssinadas && filtroAtual?.nomeFiltroEscaninho) {
            filtro = FiltroStore.filtrosEscaninho.find((f: any) =>
                f?.nome === filtroAtual.nomeFiltroEscaninho
                && f?.localDoFiltro === 'minutasAssinadas'
            );
        }

        if (!filtro) {
            return {};
        }
        const dadosSalvos = typeof filtro.dados === 'string' ? JSON.parse(filtro.dados) : filtro.dados;

        if (RouterStore.isMinutasAssinadas) {
            const baseMinutas = FiltroStore.filtroAplicadoMinutasAssinadasDados || dadosSalvos;
            const filtroSalvoNormalizado = this.limparDadosFiltro(this.dadosFiltroMinutasAssinadas(baseMinutas, false));
            const filtroAtualNormalizado = this.limparDadosFiltro(this.dadosFiltroMinutasAssinadas(filtroAtual, false));
            return {
                diff: this.retornaDiferencas(filtroSalvoNormalizado as any, filtroAtualNormalizado as any),
                filtroSalvoNormalizado,
                filtroAtualNormalizado
            };
        }

        const filtroSalvoNormalizado = this.limparDadosFiltro(
            FiltroEscaninho.inicializado(dadosSalvos as any)
        ) as FiltroEscaninho;
        const filtroAtualNormalizado = this.limparDadosFiltro(
            FiltroEscaninho.inicializado(filtroAtual)
        ) as FiltroEscaninho;
        this.removerValoresDefaultSomenteVisuais(filtroSalvoNormalizado);
        this.removerValoresDefaultSomenteVisuais(filtroAtualNormalizado);
        return {
            diff: this.retornaDiferencas(filtroSalvoNormalizado, filtroAtualNormalizado),
            filtroSalvoNormalizado,
            filtroAtualNormalizado
        };
    }

    private removerValoresDefaultSomenteVisuais = (filtro: FiltroEscaninho) => {
        if (filtro.possuiPeticaoPendente
            && Array.isArray(filtro.tipoPeticaoSelecionadaFilter)
            && filtro.tipoPeticaoSelecionadaFilter.length === 1
            && filtro.tipoPeticaoSelecionadaFilter[0] === 'Qualquer tipo') {
            filtro.tipoPeticaoSelecionadaFilter = [];
        }
        if (filtro.possuiImpedimento
            && Array.isArray(filtro.motivoImpedimentoSelecionadoFilter)
            && filtro.motivoImpedimentoSelecionadoFilter.length === 1
            && filtro.motivoImpedimentoSelecionadoFilter[0] === 'Qualquer tipo') {
            filtro.motivoImpedimentoSelecionadoFilter = [];
        }
    }

    retornaDiferencas = (f1: FiltroEscaninho, f2: FiltroEscaninho) => {
        f1.dados = JSON.stringify(f1);
        f2.dados = JSON.stringify(f2);

        if (f1 === f2) {
            return {};
        } else {
            return DeepDiffMapper.map((JSON.parse(f1.dados)), (JSON.parse(f2.dados)));
        }
    }

    nomeAmigavel = (nomeFiltroSistema: string) => {
        let retorno = '';
        switch (nomeFiltroSistema) {
            case 'possuiResponsavel':
                retorno = 'Possui Responsável';
                break;
            case 'naoPossuiResponsavel':
                retorno = 'Não Possui Responsável';
                break;
            case 'possuiRedatorDesignado':
                retorno = 'Possui redator designado';
                break;
            case 'processoRetornandoAoGabinete':
                retorno = 'Processo retornando ao Gabinete';
                break;
            case 'foraDoGabineteFilter':
                retorno = 'Fora do fluxo do GE';
                break;
            case 'incluirForaDoGabineteFilter':
                retorno = 'Fora do fluxo do GE';
                break;
            case 'possuiMinutaDespacho':
                retorno = 'Possui Despacho';
                break;
            case 'possuiMinutaDecisao':
                retorno = 'Possui Decisão';
                break;
            case 'possuiMinutaVoto':
                retorno = 'Possui Voto';
                break;
            case 'possuiMinutaVotoAgravoProvido':
                retorno = "Possui Voto com RR"
                break;
            case 'naoPossuiMinuta':
                retorno = 'Não Possui Minuta';
                break;
            case 'julgadoFilter':
                retorno = 'Processo Julgado';
                break;
            case 'naoJulgadoFilter':
                retorno = 'Processo Não Julgado';
                break;
            case 'tipoJulgamentoPresencialFilter':
                retorno = 'Julgamento Presencial';
                break;
            case 'tipoJulgamentoVirtualFilter':
                retorno = 'Julgamento Virtual';
                break;
            case 'publicavelFilter':
                retorno = 'Minuta Publicável';
                break;
            case 'naoPublicavelFilter':
                retorno = 'Minuta Não Publicável';
                break;
            case 'favoritoFilter':
                retorno = 'Processo Favorito';
                break;
            case 'naoFavoritoFilter':
                retorno = 'Processo Não Favorito';
                break;
            case 'votoLiberadoFilter':
                retorno = 'Voto Liberado';
                break;
            case 'votoNaoLiberadoFilter':
                retorno = 'Voto não Liberado';
                break;
            case 'possuiPeticaoPendente':
                retorno = 'Possui Petição Pendente';
                break;
            case 'naoPossuiPeticaoPendente':
                retorno = 'Não Possui Petição Pendente';
                break;
            case 'possuiImpedimento':
                retorno = 'Possui Impedimento';
                break;
            case 'assinavel':
                retorno = 'Minuta Assinável';
                break;
            case 'orgaoJudicanteSelecionadoFilter':
                retorno = 'Órgão Julgador Colegiado';
                break;
            case 'andTramTarefasSelecionadoFilter':
                retorno = 'Andamento/Tramitação/Tarefas/Movimento CNJ';
                break;
            case 'tipoPeticaoSelecionadaFilter':
                retorno = 'Tipo de Petição';
                break;
            case 'motivoImpedimentoSelecionadoFilter':
                retorno = 'Motivo de Impedimento';
                break;
            case 'tarefaSelecionadaFilter':
                retorno = 'Movimentação';
                break;
            case 'usuarioSelecionadoFilter':
                retorno = 'Responsável';
                break;
            case 'etiquetaSelecionadaFilter':
                retorno = 'Etiqueta';
                break;
            case 'indicadorSelecionadoFilter':
                retorno = 'Indicadores';
                break;
            case 'categoriaProcessoFilter':
                retorno = 'Categoria';
                break;
            case 'situacaoMinutaFilter':
                retorno = 'Situação da minuta';
                break;
            case 'unidadePublicadoraFilter':
                retorno = 'Unidade Publicadora';
                break;
            case 'resultadoJulgamentoFilter':
                retorno = 'Resultado Julgamento';
                break;
            case 'listaDeNumeroProcessoFilter':
                retorno = 'Lista de processos';
                break;
            case 'textoTriagemFilter':
                retorno = 'Triagem Livre';
                break;
            case 'tipoMinutaFilter':
                retorno = 'Minutas do tipo';
                break;
            case 'filtroTipoMinuta':
                retorno = 'Tipo de Minuta';
                break;
            case 'dataInicioMinutaAssinadaFiltro':
                retorno = 'Data início';
                break;
            case 'dataFimMinutaAssinadaFiltro':
                retorno = 'Data fim';
                break;
            case 'classeProcessoFilter':
                retorno = 'Classe';
                break;
            case 'temaProcessoFilter':
                retorno = 'Tema';
                break;
            case 'classeInteiraFilter':
                retorno = 'Classe';
                break;
            case 'classeInicialProcessoFilter':
                retorno = 'Classe Inicial';
                break;
            case 'numeroProcessoFilter':
                retorno = 'Numero';
                break;
            case 'digitoProcessoFilter':
                retorno = 'Digito';
                break;
            case 'anoProcessoFilter':
                retorno = 'Ano';
                break;
            case 'orgaoProcessoFilter':
                retorno = 'Orgao';
                break;
            case 'regionalProcessoFilter':
                retorno = 'Regional';
                break;
            case 'varaProcessoFilter':
                retorno = 'Vara';
                break;
            case 'assuntoFilter':
                retorno = 'Assuntos';
                break;
            case 'nomeParteFilter':
                retorno = 'Nome Da Parte';
                break;
            case 'parteFilter':
                retorno = 'Parte';
                break;
            case 'pautaFilter':
                retorno = 'Pauta de Julgamento';
                break;
            case 'camposDeTriagemBooleanFilter':
                retorno = 'Campos de triagem';
                break;
            case 'camposDeResponsavelPelaTriagemFilter':
                retorno = 'Responsável pela triagem';
                break;
            case 'camposDeTriagemValorCondenacaoFilter':
                retorno = 'Valor Condenação';
                break;
            case 'movimentosFilter':
                retorno = 'Movimentos/Códigos de Decisão';
                break;
            case 'origem':
                retorno = 'Origem do Processo';
                break;
            case 'anoAutuacaoFilter':
                retorno = 'Ano de Autuação no TST';
                break;
            case 'situacaoPeticaoFilter':
                retorno = 'Situacao da peticao';
                break;
            case 'usuariosUltimaAlteracaoSituacaoMinutaFilter':
                retorno = 'Último a modificar';
                break;
            case 'relatorProcessoFilter':
                retorno = 'Relator';
                break;
            case 'correJuntoFilter':
                retorno = 'Com Corre Junto';
                break;
            case 'associadoFilter':
                retorno = 'Com Processo Associado';
                break;
            case 'relator':
                retorno = 'Relator do Processo';
                break;
        }
        return retorno;
    }
}

export default new FiltroService();
